import { useCallback, useContext } from 'react';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import { sendSourcedFavoriteMessageToNative } from '~/native/common/dispatchers';
import { removeFavorite } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { FavoritesArguments } from './types';

type LifeCycleFn = (args: FavoritesArguments) => void;

type Options = {
  beforeRemoveFavorite?: LifeCycleFn;
};

const dummyFn = () => {};

export default function useRemoveFavorite({
  beforeRemoveFavorite = dummyFn
}: Options = {}) {
  const {
    favorites: { setData, setLoading },
    triggerEvent
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();

  return useCallback(
    async (args: FavoritesArguments) => {
      const { nativeEventSource, stockNumber, vehicleId } = args;

      if (!isLoggedIn) return;

      try {
        beforeRemoveFavorite(args);

        setLoading();
        await removeFavorite(vehicleId);

        if (nativeEventSource)
          sendSourcedFavoriteMessageToNative(
            'remove',
            {
              stockNumber: stockNumber ?? 0,
              vehicleId: vehicleId
            },
            nativeEventSource
          );

        setData(vehicles => vehicles.filter(v => v.vehicleId !== vehicleId));

        triggerEvent.favoriteRemove();
      } catch (err) {
        logError(err, { location: 'Remove favorite' });
      }
    },
    [beforeRemoveFavorite, isLoggedIn, setData, setLoading, triggerEvent]
  );
}
